import { gsap } from "gsap";
import { type ReactNode, useLayoutEffect, useRef } from "react";

import { ColocaNetworkIcon, ColocaTokenIcon } from "@/shared/components/data-display/ColocaCryptoIcon";
import { ColocaIsotype } from "@/shared/components/data-display/ColocaIsotype";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import { cn } from "@/shared/lib/utils";
import { networkBrandColor } from "@/shared/utils/cryptoNetworks";

import { MOTION_DURATION, MOTION_EASE, MOTION_STAGGER } from "./motion.tokens";

/** What a coin carries on a leg: money with a currency symbol, or a token with its logo. */
export type MoneyTransitAsset = { kind: "fiat"; currency: string } | { kind: "token"; symbol: string };

/** How a leg moves money: a bank rail, or a chain, drawn as a stream of bits in the chain's colour. */
export type MoneyTransitRail = { kind: "fiat" } | { kind: "chain"; network: string; label: string };

export interface MoneyTransitLeg {
  rail: MoneyTransitRail;
  asset: MoneyTransitAsset;
}

export interface MoneyTransitEnd {
  icon: ReactNode;
  label: string;
  caption?: ReactNode;
  /** `brand` paints the tile in the primary colour, for Coloca itself. */
  tone?: "brand" | "plain";
}

export interface ColocaMoneyTransitProps {
  from: MoneyTransitEnd;
  to: MoneyTransitEnd;
  /**
   * One leg, or two meeting at Coloca, where the money changes form: the coin leaves as pesos and goes on as
   * USDT. Each leg is drawn by its own rail.
   */
  legs: readonly [MoneyTransitLeg] | readonly [MoneyTransitLeg, MoneyTransitLeg];
  /** The whole picture in words, for assistive tech. */
  label: string;
  className?: string;
}

const COINS = 2;
/** One period of the fiat rail's dashes, in px: the background moves by exactly this much per loop. */
const DASH_PERIOD = 12;

/** A fixed byte stream per leg: the chain's texture, not data. Two copies make the loop seamless. */
function bitStream(seed: number, bytes = 10): string {
  let state = seed;
  const out: string[] = [];
  for (let index = 0; index < bytes; index += 1) {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    out.push(((state >> 8) & 0xff).toString(2).padStart(8, "0"));
  }
  return `${out.join(" ")} `;
}

const BITS = [bitStream(0x2c0ca), bitStream(0x51ab3)] as const;

function currencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    return parts.find((part) => part.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * Money on its way, for the wait after an operation was authorized: coins leave the origin, cross Coloca when
 * the money changes form, and land in the destination, which nods at each arrival. A chain leg is a stream of
 * binary in the chain's colour with its logo under it, and the token rides it. It loops for as long as it is
 * mounted, because the wait has no known end; it says nothing about progress.
 *
 * Static (and complete) with reduced motion: one coin per leg, halfway.
 */
export function ColocaMoneyTransit({ from, to, legs, label, className }: ColocaMoneyTransitProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const twoLegs = legs.length === 2;

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reduced) return;
    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(root);

      const intro = gsap.timeline();
      intro.fromTo(
        q("[data-end]"),
        { opacity: 0, y: 8 },
        {
          opacity: 1,
          y: 0,
          duration: MOTION_DURATION.enter,
          ease: MOTION_EASE.enter,
          stagger: MOTION_STAGGER.loose,
        },
      );
      intro.fromTo(
        q("[data-rail]"),
        { scaleX: 0, transformOrigin: "0% 50%" },
        {
          scaleX: 1,
          duration: MOTION_DURATION.enter,
          ease: MOTION_EASE.enter,
          stagger: MOTION_STAGGER.loose,
        },
        0.15,
      );
      intro.fromTo(
        q("[data-hub]"),
        { scale: 0.6, opacity: 0, transformOrigin: "50% 50%" },
        { scale: 1, opacity: 1, duration: MOTION_DURATION.slow, ease: MOTION_EASE.pop },
        0.35,
      );

      // The rails move the way the money does: dashes slide, bits stream, left to right.
      const dashes = q("[data-dashes]");
      if (dashes.length) {
        gsap.fromTo(
          dashes,
          { backgroundPosition: "0px 0px" },
          { backgroundPosition: `${DASH_PERIOD}px 0px`, duration: 0.6, ease: MOTION_EASE.linear, repeat: -1 },
        );
      }
      const bits = q("[data-bits]");
      if (bits.length) {
        gsap.fromTo(
          bits,
          { xPercent: -50 },
          { xPercent: 0, duration: 9, ease: MOTION_EASE.linear, repeat: -1 },
        );
      }

      const hubRing = q("[data-hub-ring]")[0];
      const target = q("[data-target]")[0];
      const travel = twoLegs ? 3.2 : 2.4;
      const gap = 0.4;
      const period = travel + gap;

      q("[data-coin]").forEach((coin, index) => {
        const body = coin.querySelector("[data-coin-body]");
        const [first, second] = Array.from(coin.querySelectorAll("[data-face]"));
        if (!body || !first) return;
        const loop = gsap.timeline({
          repeat: -1,
          repeatDelay: gap,
          delay: 0.9 + (index * period) / COINS,
        });
        loop
          .set(coin, { left: "0%" }, 0)
          .set(first, { opacity: 1 }, 0)
          .fromTo(
            body,
            { scale: 0.5, opacity: 0 },
            { scale: 1, opacity: 1, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter },
            0,
          );
        if (twoLegs && second) {
          // At Coloca the coin changes face (pesos ⇄ USDT) and the hub ripples.
          loop
            .set(second, { opacity: 0 }, 0)
            .to(coin, { left: "50%", duration: travel / 2, ease: "power1.in" }, 0)
            .addLabel("hub")
            .to(first, { opacity: 0, duration: MOTION_DURATION.quick }, "hub-=0.1")
            .to(second, { opacity: 1, duration: MOTION_DURATION.quick }, "hub-=0.1")
            .to(coin, { left: "100%", duration: travel / 2, ease: "power1.out" }, "hub");
          if (hubRing) {
            loop.fromTo(
              hubRing,
              { scale: 1, opacity: 0.5 },
              {
                scale: 1.9,
                opacity: 0,
                duration: MOTION_DURATION.enter,
                ease: MOTION_EASE.enter,
                immediateRender: false,
              },
              "hub",
            );
          }
        } else {
          loop.to(coin, { left: "100%", duration: travel, ease: "power1.inOut" }, 0);
        }
        // The coin's last move ends the leg; it fades into the destination, which nods.
        const arrive = loop.duration();
        loop.to(
          body,
          { scale: 0.5, opacity: 0, duration: MOTION_DURATION.quick, ease: "power1.in" },
          arrive - MOTION_DURATION.quick,
        );
        if (target) {
          loop.fromTo(
            target,
            { scale: 1 },
            {
              scale: 1.07,
              duration: 0.14,
              yoyo: true,
              repeat: 1,
              ease: "power1.out",
              immediateRender: false,
            },
            arrive - 0.1,
          );
        }
      });
    }, root);
    return () => ctx.revert();
  }, [reduced, twoLegs]);

  return (
    <div ref={ref} role="img" aria-label={label} className={cn("@container w-full select-none", className)}>
      {/* Sized by the space it is given, not the viewport: two columns of a wide page can still be narrow. */}
      <div className="flex items-start gap-1.5 @md:gap-2">
        <End end={from} />
        <div className="relative h-12 min-w-0 flex-1">
          {legs.map((leg, index) => (
            <Rail
              key={index}
              leg={leg}
              bits={BITS[index] ?? BITS[0]}
              className={twoLegs ? (index === 0 ? "right-1/2 left-0" : "right-0 left-1/2") : "inset-x-0"}
              captionAt={twoLegs ? (index === 0 ? "25%" : "75%") : "50%"}
            />
          ))}
          {twoLegs && (
            <span className="absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
              <span
                data-hub-ring
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-primary opacity-0"
              />
              <span
                data-hub
                className="relative flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm"
              >
                <ColocaIsotype className="size-4" />
              </span>
            </span>
          )}
          {Array.from({ length: COINS }, (_, index) => (
            <Coin key={index} legs={legs} still={stillCoin(index, twoLegs)} />
          ))}
        </div>
        <End end={to} target />
      </div>
    </div>
  );
}

/** Where each coin rests without motion: one per leg, halfway along it; the third is not shown. */
function stillCoin(index: number, twoLegs: boolean): { left: string; face: number } | null {
  if (twoLegs) return index === 0 ? { left: "25%", face: 0 } : index === 1 ? { left: "75%", face: 1 } : null;
  return index === 0 ? { left: "50%", face: 0 } : null;
}

function End({ end, target = false }: { end: MoneyTransitEnd; target?: boolean }) {
  return (
    <div data-end className="flex w-16 shrink-0 flex-col items-center gap-1 text-center @md:w-24">
      <span
        data-target={target ? "" : undefined}
        className={cn(
          "flex size-12 items-center justify-center rounded-2xl",
          end.tone === "brand" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
        )}
      >
        {end.icon}
      </span>
      <span className="mt-0.5 line-clamp-2 w-full text-xs leading-tight font-medium break-words text-foreground">
        {end.label}
      </span>
      {end.caption && (
        <span className="w-full truncate text-[11px] text-muted-foreground">{end.caption}</span>
      )}
    </div>
  );
}

function Rail({
  leg,
  bits,
  className,
  captionAt,
}: {
  leg: MoneyTransitLeg;
  bits: string;
  className: string;
  captionAt: string;
}) {
  if (leg.rail.kind === "fiat") {
    return (
      <span
        data-rail
        aria-hidden="true"
        className={cn("absolute top-1/2 h-0.5 -translate-y-1/2 overflow-hidden text-primary/45", className)}
      >
        <span
          data-dashes
          className="block size-full"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, currentColor 0 6px, transparent 6px ${DASH_PERIOD}px)`,
          }}
        />
      </span>
    );
  }

  const brand = networkBrandColor(leg.rail.network) ?? "var(--color-primary)";
  // The bits mix the chain's colour with the text colour: a yellow chain stays legible on white, a purple one
  // on the dark theme. The band behind them is the pure brand colour, faint.
  const ink = `color-mix(in oklab, ${brand} 72%, var(--color-foreground))`;
  return (
    <>
      <span
        data-rail
        aria-hidden="true"
        className={cn(
          "absolute top-1/2 h-5 -translate-y-1/2 overflow-hidden rounded-full",
          "[mask-image:linear-gradient(90deg,transparent,black_14%,black_86%,transparent)]",
          className,
        )}
        style={{ color: ink }}
      >
        <span className="absolute inset-0 opacity-15" style={{ backgroundColor: brand }} />
        <span
          data-bits
          className="absolute top-1/2 left-0 -translate-y-1/2 font-mono text-[9px] leading-none font-semibold tracking-[0.18em] whitespace-nowrap"
        >
          {bits}
          {bits}
        </span>
      </span>
      <span
        className="absolute top-full mt-1.5 inline-flex -translate-x-1/2 items-center gap-1 text-[11px] font-medium whitespace-nowrap text-muted-foreground"
        style={{ left: captionAt }}
      >
        <ColocaNetworkIcon network={leg.rail.network} size={12} />
        {leg.rail.label}
      </span>
    </>
  );
}

function Coin({
  legs,
  still,
}: {
  legs: ColocaMoneyTransitProps["legs"];
  still: { left: string; face: number } | null;
}) {
  return (
    <span
      data-coin
      aria-hidden="true"
      className="absolute top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
      style={{ left: still?.left ?? "0%" }}
    >
      <span data-coin-body className="relative block size-6" style={{ opacity: still ? 1 : 0 }}>
        {legs.map((leg, index) => (
          <span
            key={index}
            data-face
            className="absolute inset-0"
            style={{ opacity: (still?.face ?? 0) === index ? 1 : 0 }}
          >
            <CoinFace asset={leg.asset} />
          </span>
        ))}
      </span>
    </span>
  );
}

function CoinFace({ asset }: { asset: MoneyTransitAsset }) {
  if (asset.kind === "fiat") {
    const symbol = currencySymbol(asset.currency);
    return (
      <span
        className={cn(
          "flex size-6 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground shadow-sm ring-2 ring-primary/25",
          symbol.length > 1 ? "text-[9px]" : "text-xs",
        )}
      >
        {symbol}
      </span>
    );
  }
  return (
    <span className="block size-6 rounded-full shadow-sm">
      <ColocaTokenIcon
        symbol={asset.symbol}
        size={24}
        fallback={
          <span className="flex size-6 items-center justify-center rounded-full bg-muted text-[8px] font-bold">
            {asset.symbol}
          </span>
        }
      />
    </span>
  );
}
